import { Component, ErrorInfo, ReactNode } from "react";
import styled from "styled-components";
import { COLORS } from "../styled";

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 100vh;
  padding: 24px;
  background-color: ${COLORS.BLUE_LIGHT};
  text-align: center;
`;

const Title = styled.h2`
  margin: 0 0 12px;
  font-size: 18px;
  color: #333;
`;

const Message = styled.p`
  margin: 0 0 20px;
  font-size: 14px;
  color: #666;
  max-width: 360px;
`;

const RetryButton = styled.button`
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: #1890ff;
  border: none;
  border-radius: 6px;
  cursor: pointer;

  &:hover {
    background: #40a9ff;
  }
`;

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ChunkLoadErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    console.error("ChunkLoadErrorBoundary caught:", error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
    window.location.reload();
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children } = this.props;
    if (hasError) {
      const isChunkError = error?.name === "ChunkLoadError"
        || /Loading chunk \d+ failed/i.test(error?.message ?? "");

      return (
        <Wrapper>
          <Title>{isChunkError ? "Page failed to load" : "Something went wrong"}</Title>
          <Message>
            {isChunkError
              ? "The page could not be loaded. This can happen after an update or due to a network issue."
              : error?.message ?? "An unexpected error occurred."}
          </Message>
          <RetryButton type="button" onClick={this.handleRetry}>
            Retry
          </RetryButton>
        </Wrapper>
      );
    }
    return children;
  }
}
